import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import {
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import {
  DeviceTrendPoints,
  QueryTrendsRequestBody,
  QueryTrendsResult,
  TrendsResult,
} from '@repo/sdk/models/components';

import { chartReadingsAtom, chartSettingsAtom } from '@/features/trends/state/chart.state';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useGaiaClient } from '@/hooks/use-gaia-client';
import { listDeviceTrendPointsQuery } from '@/queries/gaia-api/list-device-trend-points-query';
import { queryTrendsQuery } from '@/queries/gaia-api/query-trends-query';
import {
  transformTrends,
  TrendsRenderResult,
  trendsStreamQuery,
} from '@/queries/gaia-api/trends-stream-query';

import { useWebsocketQuery } from '../websockets';

interface UseTrendsRenderProps {
  chartId: string;
  groupId: string;
}
/**
 * Fetches the current Trends data for this Agent from queryCache. This does
 * not make an HTTP request, instead it reads objects from queryCache that are populated
 * by listening to a subscription to the Trends data.
 *
 * Reads from the group-level query key and filters to only this chart's sources
 * via a per-chart `select` function before applying the standard transform.
 */
export const useTrendsRender = ({
  chartId,
  groupId,
}: UseTrendsRenderProps): UseQueryResult<TrendsRenderResult | null> => {
  const { agentId } = useCurrentFacility();
  const readings = useAtomValue(useMemo(() => chartReadingsAtom(chartId), [chartId]));
  const settings = useAtomValue(useMemo(() => chartSettingsAtom(chartId), [chartId]));

  // Source IDs belonging to THIS chart
  const chartSourceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of readings ?? []) ids.add(r.displayRef.ref.id);
    for (const s of settings ?? []) ids.add(s.displayRef.ref.id);
    return ids;
  }, [readings, settings]);

  // Spread the base query config, then override queryKey and select
  const { queryKey, select: _defaultSelect, ...rest } = trendsStreamQuery({ agentId });

  return useWebsocketQuery<TrendsResult | null, TrendsRenderResult>({
    ...rest,
    queryKey: [...queryKey, 'group', groupId],
    select: (result: TrendsResult | null) => {
      if (!result) return { interval: 0, series: [] };

      // Filter values to only this chart's sources
      const filteredValues: Record<string, any> = {};
      if (result.values) {
        for (const [sourceId, data] of Object.entries(result.values)) {
          if (chartSourceIds.has(sourceId)) {
            filteredValues[sourceId] = data;
          }
        }
      }

      // Apply the standard transform to the filtered result
      return transformTrends({ ...result, values: filteredValues });
    },
  });
};

export interface UseQueryTrendsProps {
  chartId: string;
  body: QueryTrendsRequestBody;
}

/**
 * Hook for fetching trend data via the query_trends POST endpoint.
 * Uses hybrid cache key: live queries are isolated per chart; historical
 * queries are deduplicated by request body.
 * Pass a stable `body` (e.g. from useMemo with primitive deps) when possible
 * so the query options are not recomputed every render.
 */
export const useQueryTrends = ({
  chartId,
  body,
}: UseQueryTrendsProps): UseQueryResult<QueryTrendsResult> => {
  const client = useGaiaClient();
  const { agentId } = useCurrentFacility();

  const options = useMemo(
    () => queryTrendsQuery({ agentId, chartId, body, client }),
    [agentId, chartId, body, client]
  );
  return useQuery(options);
};

/**
 * Hook for fetching device trend points from Gaia BFF.
 * This hook retrieves available data points for trending for a given agent.
 *
 * @returns Query result containing device trend points with their associated display refs
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useListDeviceTrendPoints();
 *
 *  data contains DeviceTrendPoints[] with structure:
 *  {
 *    deviceId: string,
 *    alias: string,
 *    displayName: string,
 *    allDisplayRefs: DisplayRef[],      // All available trend points
 *    templateDisplayRefs: DisplayRef[]  // Template-specific trend points
 *  }
 * ```
 */
export const useListDeviceTrendPoints = (): UseSuspenseQueryResult<
  DeviceTrendPoints[] | undefined
> => {
  const client = useGaiaClient();
  const { agentId } = useCurrentFacility();

  return useSuspenseQuery(
    listDeviceTrendPointsQuery({
      agentId: agentId,
      client,
    })
  );
};
